import { useState, useEffect, useCallback, useMemo } from 'react';
import {
	Button,
	Dropdown,
	Input,
	Table,
	Tag,
	message,
	Switch,
	Modal,
	Form,
} from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import getSymbolFromCurrency from 'currency-symbol-map';
import dayjs from 'dayjs';
import get from 'lodash/get';
import { Link } from 'react-router-dom';
import JobTitleComponent from 'src/_shared/components/JobTitleComponent.jsx';
import Spinner from 'src/_shared/components/spinner/SpinnerComponent.jsx';
import {
	conditionalStatusStyle,
	formatDate,
	initialCaps,
	mapBonusStatus,
	mapReferralStatus,
	ml,
} from 'src/_shared/services/utils.js';
import { BONUS_STATUS, USER_ROLES } from '../constants';
import {
	updateBonusStatus,
	fetchEligibilityResults,
} from 'src/_shared/referral/referralActions';
import BonusDetailsModal from './BonusTableDetailsModalComponent';
import { generateBonusLogs } from 'src/_shared/api/components/logging/LoggingUtils.jsx';

const StatusUpdateModal = ({
	visible,
	onCancel,
	onConfirm,
	bonusData,
	targetStatus,
	currentUser,
	allMultiLingualData,
	ignoreEligibilityChecks,
	onIgnoreEligibilityChange,
	form,
	awaitingResponse,
}) => {
	const requiresReason = bonusData?.referralId;
	return (
		<Modal
			title={ml('Confirm Status Update', currentUser, allMultiLingualData)}
			visible={visible}
			onCancel={onCancel}
			onOk={() => form.submit()}
			okText={ml('Confirm', currentUser, allMultiLingualData)}
			cancelText={ml('Cancel', currentUser, allMultiLingualData)}
			destroyOnClose
			confirmLoading={awaitingResponse}
		>
			<Form
				form={form}
				layout="vertical"
				onFinish={(values) => onConfirm(values)}
			>
				<div style={{ marginBottom: '16px' }}>
					<strong>
						{ml(
							'Are you sure you want to update the status to',
							currentUser,
							allMultiLingualData
						)}{' '}
						<span style={conditionalStatusStyle(targetStatus)}>
							{ml(
								mapBonusStatus(targetStatus, currentUser?.company),
								currentUser,
								allMultiLingualData
							)}
						</span>
						?
					</strong>
				</div>

				{requiresReason && (
					<>
						<Form.Item
							label={
								<strong>
									{ml('Reason for Change', currentUser, allMultiLingualData)}
								</strong>
							}
							name="reasonForChange"
							rules={[
								{
									required: true,
									message: ml(
										'Please provide a reason for change',
										currentUser,
										allMultiLingualData
									),
								},
								{
									whitespace: true,
									message: ml(
										'Reason cannot be empty',
										currentUser,
										allMultiLingualData
									),
								},
							]}
						>
							<Input.TextArea
								placeholder={ml(
									'Provide a reason for change',
									currentUser,
									allMultiLingualData
								)}
								rows={3}
								maxLength={500}
								showCount
							/>
						</Form.Item>

						<Form.Item
							label={
								<strong>
									{ml(
										'Ignore Eligibility Checks',
										currentUser,
										allMultiLingualData
									)}
								</strong>
							}
							name="ignoreEligibilityChecks"
							valuePropName="checked"
							initialValue={ignoreEligibilityChecks[bonusData?.id] || false}
						>
							<div
								style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
							>
								<Switch
									size="small"
									checked={ignoreEligibilityChecks[bonusData?.id] || false}
									onChange={(checked) =>
										onIgnoreEligibilityChange(bonusData?.id, checked)
									}
								/>
								<div
									style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}
								>
									{ml(
										'Ignore any further automated eligibility checks for this bonus.',
										currentUser,
										allMultiLingualData
									)}
								</div>
							</div>
						</Form.Item>
					</>
				)}
			</Form>
		</Modal>
	);
};

const BonusTable = (props) => {
	const {
		bonuses,
		filterByReferralId,
		filteredReferralId,
		company,
		theme,
		setExportData,
		sortByAlph,
		currentUser,
		allMultiLingualData,
		// Redux actions and state
		updateBonusStatus,
		fetchEligibilityResults,
		fetchingEligibility,
	} = props;

	const [tableBonuses, setTableBonuses] = useState(bonuses);
	const [selectedBonusId, setSelectedBonusId] = useState(null);
	const [modalVisible, setModalVisible] = useState(false);
	const [visibility, setVisibility] = useState({});
	const [awaitingResponse, setAwaitingResponse] = useState(false);
	const [modalLoading, setModalLoading] = useState(false);
	const [statusUpdateModal, setStatusUpdateModal] = useState({
		visible: false,
		bonusData: null,
		targetStatus: null,
		statusColor: '#666',
	});
	const [form] = Form.useForm();

	const ignoreEligibilityChecksValue = {};

	bonuses.map((bonus) => {
		ignoreEligibilityChecksValue[bonus.id] =
			bonus.ignoreEligibilityChecks || false;
	});
	const [ignoreEligibilityChecks, setIgnoreEligibilityChecks] = useState(
		ignoreEligibilityChecksValue
	);

	const headers = useMemo(
		() => ({
			name: ml('Name', currentUser, allMultiLingualData),
			recipientType: ml('Recipient Type', currentUser, allMultiLingualData),
			job: ml('Job', currentUser, allMultiLingualData),
			referral: ml('Referral', currentUser, allMultiLingualData),
			bonusAmount: ml('Bonus Amount', currentUser, allMultiLingualData),
			elligibleDate: ml('Eligible Date', currentUser, allMultiLingualData),
			payment: ml('Payment', currentUser, allMultiLingualData),
			status: ml('Status', currentUser, allMultiLingualData),
			details: ml('Details', currentUser, allMultiLingualData),
		}),
		[currentUser, allMultiLingualData]
	);

	const ml_of = useMemo(
		() => ml('of', currentUser, allMultiLingualData),
		[currentUser, allMultiLingualData]
	);

	const handleOpenDetailsModal = useCallback(
		(bonus) => {
			setSelectedBonusId(bonus.id);

			if (bonus.referralId && !bonus.referral?.eligibilityResults) {
				setModalLoading(true);
				fetchEligibilityResults(bonus.referralId, bonus.id)
					.then(() => {
						setModalLoading(false);
						setModalVisible(true);
					})
					.catch((error) => {
						console.error('Error fetching eligibility results:', error);
						setModalLoading(false);
						setModalVisible(true);
					});
			} else {
				setModalVisible(true);
			}
		},
		[fetchEligibilityResults]
	);

	const selectedBonus = useMemo(() => {
		return tableBonuses.find((bonus) => bonus.id === selectedBonusId) || null;
	}, [tableBonuses, selectedBonusId]);

	const handleCloseModal = useCallback(() => {
		setModalVisible(false);
		setSelectedBonusId(null);
	}, []);

	const isPromptDisabled = (bonusStatus, targetKey) => {
		if (BONUS_STATUS.hasOwnProperty(bonusStatus)) {
			return BONUS_STATUS[bonusStatus] === BONUS_STATUS[targetKey];
		}
		return false;
	};

	const handleIgnoreEligibilityChange = useCallback((bonusId, checked) => {
		setIgnoreEligibilityChecks((prev) => ({
			...prev,
			[bonusId]: checked,
		}));
	}, []);

	const handleVisibleChange = useCallback((key, flag) => {
		setVisibility((prev) => ({
			...prev,
			[key]: flag,
		}));
	}, []);

	const getPaymentLink = useCallback(
		(payment, referralId) => {
			const payString = payment === null ? '' : payment;
			const splitLink = payString.split('');
			const lastChar = splitLink.pop();
			const label = splitLink.join('').replace('of', ml_of);

			return (
				<div>
					{label}{' '}
					<Button
						type="link"
						className="btn-link px-1"
						style={{ height: 'auto' }}
						onClick={() => filterByReferralId(referralId)}
					>
						{lastChar}
					</Button>
				</div>
			);
		},
		[ml_of, filterByReferralId]
	);

	const getExportData = useCallback(
		async (data) => {
			if (!data || data.length === 0) return;

			const languageCode = currentUser.languageCode ?? 'US';
			const newData = [];

			for (const bonus of data) {
				newData.push(bonus);
			}

			const detailHeaders = {
				currency: ml('Currency', currentUser, allMultiLingualData),
				title: ml('Referring Employee Title', currentUser, allMultiLingualData),
				companyNumber: ml(
					'Referring Employee Company Number',
					currentUser,
					allMultiLingualData
				),
				employeeNumber: ml(
					'Referring Employee Number',
					currentUser,
					allMultiLingualData
				),
				referredNumber: ml(
					'Referred Employee Number',
					currentUser,
					allMultiLingualData
				),
				status: ml(
					'Referring Employee Status',
					currentUser,
					allMultiLingualData
				),
				hiredDate: ml(
					`${mapReferralStatus('hired', currentUser.company)} Date`,
					currentUser,
					allMultiLingualData
				),
				startDate: ml('Start Date', currentUser, allMultiLingualData),
				eligibleDate: ml('Eligible Date', currentUser, allMultiLingualData),
				paidDate: ml('Paid Date', currentUser, allMultiLingualData),
				logs: ml('Logs', currentUser, allMultiLingualData),
			};
			const ml_active = ml('Active', currentUser, allMultiLingualData);
			const ml_inactive = ml('Inactive', currentUser, allMultiLingualData);

			const includeCompanyNumber = newData.some(
				(record) => record.user?.accountClaim?.companyNumber
			);
			const includeEmployeeNumber = newData.some(
				(record) => record.user?.accountClaim?.employeeId
			);
			const includeReferredNumber = newData.some(
				(record) => record.contact?.accountClaim?.employeeId
			);

			const parseName = (record) => {
				const userType =
					record.recipientType === 'employee' ? 'user' : 'contact';
				return `${record[userType]?.firstName ?? ''} ${record[userType]?.lastName ?? ''}`;
			};

			const parseBonus = (record) => {
				const formattedAmount =
					record.amountDue
						?.toString()
						.replaceAll(/\B(?=(\d{3})+(?!\d))/g, ',') ?? '';
				const selectedCurrency =
					record.currency ?? record.user?.userGroup?.currency ?? 'USD';
				return `${selectedCurrency}${formattedAmount}`;
			};

			const parseCurrency = (record) => {
				const currency = record.user?.userGroup?.currency ?? 'USD';
				const symbol = getSymbolFromCurrency(currency);
				return `${symbol} ${currency}`;
			};

			const exportData = newData.map((record) => {
				const row = {
					[headers.name]: parseName(record),
					[headers.recipientType]: initialCaps(record.recipientType ?? ''),
					[headers.job]: `${record.job?.title}`,
					[headers.referral]: `${record.contact?.firstName ?? ''} ${record.contact?.lastName ?? ''}`,
					[headers.bonusAmount]: parseBonus(record),
					[headers.elligibleDate]: formatDate(
						record.earnedDate,
						currentUser.languageCode,
						currentUser.dateFormat
					),
					[headers.payment]: record.payment?.replace('of', ml_of),
					[headers.status]: `${ml(mapBonusStatus(record.bonusStatus, company), currentUser, company)}`,
					[detailHeaders.currency]: parseCurrency(record),
					[detailHeaders.hiredDate]: formatDate(
						record.hiredDate,
						languageCode,
						allMultiLingualData
					),
					[detailHeaders.startDate]: formatDate(
						record.startDate,
						languageCode,
						allMultiLingualData
					),
					[detailHeaders.eligibleDate]: formatDate(
						record.earnedDate,
						languageCode,
						allMultiLingualData
					),
					[detailHeaders.paidDate]: formatDate(
						record.paidDate,
						languageCode,
						allMultiLingualData
					),
				};

				if (includeCompanyNumber) {
					row[detailHeaders.companyNumber] =
						record.user?.accountClaim?.companyNumber ?? '';
				}

				if (includeEmployeeNumber) {
					row[detailHeaders.employeeNumber] =
						record.user?.accountClaim?.employeeId ?? '';
				}

				if (includeReferredNumber) {
					row[detailHeaders.referredNumber] =
						record?.contact?.accountClaim?.employeeId ?? '';
				}

				if (record.referralId) {
					try {
						const referringUserData = record?.referral?.user || {};
						row[detailHeaders.title] = `${referringUserData?.title ?? ''}`;
						row[detailHeaders.status] = referringUserData?.active
							? ml_active
							: ml_inactive;
						row[detailHeaders.logs] =
							record?.referral?.eligibilityResults ?? 'Unavailable';
					} catch {
						row[detailHeaders.logs] = 'Unavailable';
					}
				}

				return row;
			});

			setExportData(exportData);
		},
		[currentUser, headers, ml_of, company, allMultiLingualData, setExportData]
	);

	const handleTableChange = useCallback(
		async (event) => {
			if (event.action === 'sort') {
				await getExportData(event.currentDataSource);
			}
		},
		[getExportData]
	);

	const handleModalConfirm = useCallback(
		(formValues) => {
			const { bonusData, targetStatus } = statusUpdateModal;

			setStatusUpdateModal({
				visible: false,
				bonusData: null,
				targetStatus: null,
			});
			form.resetFields();

			const updatedFields = {
				bonusStatus: targetStatus,
				paidDate:
					targetStatus === 'paid'
						? new Date().toISOString()
						: bonusData.paidDate,
				ignoreEligibilityChecks:
					ignoreEligibilityChecks?.[bonusData.id] || false,
				amountDue: bonusData.amountDue,
				startDate: bonusData.startDate,
				earnedDate: bonusData.earnedDate,
				notes: bonusData.notes,
			};

			const logs = generateBonusLogs(
				bonusData,
				updatedFields,
				currentUser.emailAddress,
				formValues.reasonForChange || ''
			);

			const bonusInput = {
				id: bonusData.id,
				bonusStatus: targetStatus,
				paidDate: targetStatus === 'paid' ? new Date().toISOString() : null,
				ignoreEligibilityChecks: ignoreEligibilityChecks[bonusData.id] || false,
			};

			setAwaitingResponse(true);
			updateBonusStatus(bonusInput, bonusData.referralId, logs)
				.then((item) => {
					setAwaitingResponse(false);
					message.success(
						ml('Status updated successfully', currentUser, allMultiLingualData)
					);
				})
				.catch((error) => {
					console.error('Error updating bonus status:', error);
					setAwaitingResponse(false);
					message.error(
						ml('Failed to update status', currentUser, allMultiLingualData)
					);
				});
		},
		[
			statusUpdateModal,
			ignoreEligibilityChecks,
			currentUser,
			updateBonusStatus,
			allMultiLingualData,
			form,
		]
	);

	const handleModalCancel = useCallback(() => {
		setStatusUpdateModal({
			visible: false,
			bonusData: null,
			targetStatus: null,
		});
		form.resetFields();
	}, [form]);

	useEffect(() => {
		if (filteredReferralId) {
			const filtered = bonuses.filter(
				(bonus) => bonus.referralId === filteredReferralId
			);
			setTableBonuses(filtered);
			getExportData(filtered);
		} else {
			const ignoreEligibilityChecksValue = {};
			bonuses.map((bonus) => {
				ignoreEligibilityChecksValue[bonus.id] =
					bonus.ignoreEligibilityChecks || false;
			});
			setIgnoreEligibilityChecks(ignoreEligibilityChecksValue);
			setTableBonuses(bonuses);
			getExportData(bonuses);
		}
	}, [bonuses, filteredReferralId, getExportData]);

	useEffect(() => {
		if (selectedBonusId && !fetchingEligibility && modalLoading) {
			setModalLoading(false);
			setModalVisible(true);
		}
	}, [selectedBonusId, fetchingEligibility, modalLoading]);

	const columns = useMemo(
		() => [
			{
				title: headers.name,
				key: 'name',
				width: '15%',
				showSorterTooltip: false,
				sorter(a, b) {
					const aFirstName =
						get(a, 'recipientType') === 'employee'
							? get(a, 'user.firstName')
							: get(a, 'contact.firstName');
					const aLastName =
						get(a, 'recipientType') === 'employee'
							? get(a, 'user.lastName')
							: get(a, 'contact.lastName');
					const bFirstName =
						get(b, 'recipientType') === 'employee'
							? get(b, 'user.firstName')
							: get(b, 'contact.firstName');
					const bLastName =
						get(b, 'recipientType') === 'employee'
							? get(b, 'user.lastName')
							: get(b, 'contact.lastName');
					return sortByAlph(aFirstName + aLastName, bFirstName + bLastName);
				},
				render(record, data) {
					const firstName =
						get(data, 'recipientType') === 'employee'
							? get(data, 'user.firstName')
							: get(data, 'contact.firstName');
					const lastName =
						get(data, 'recipientType') === 'employee'
							? get(data, 'user.lastName')
							: get(data, 'contact.lastName');
					const URL =
						get(data, 'recipientType') === 'employee'
							? `/employees/${get(data, 'userId')}`
							: `/referrals/${get(data, 'referralId')}`;
					return (
						<Link to={URL} className="table-link">
							{firstName} {lastName}
						</Link>
					);
				},
			},
			{
				title: headers.recipientType,
				dataIndex: 'recipientType',
				key: 'recipientType',
				showSorterTooltip: false,
				sorter: (a, b) => sortByAlph(a.recipientType, b.recipientType),
				render(type) {
					if (type && typeof type === 'string') {
						const value = type.split('');
						value.splice(0, 1, value[0].toUpperCase());
						return value.join('');
					}
				},
			},
			{
				title: headers.job,
				dataIndex: 'job',
				key: 'job',
				width: '16%',
				showSorterTooltip: false,
				sorter: (a, b) => sortByAlph(get(a, 'job.title'), get(b, 'job.title')),
				render: (job) =>
					job === null ? null : (
						<Link className="table-link" to={`/jobs/${job.id}`}>
							<JobTitleComponent
								jobTitle={job.title}
								currentUser={currentUser}
							/>
						</Link>
					),
			},
			{
				title: headers.referral,
				key: 'referral',
				width: '11%',
				showSorterTooltip: false,
				sorter(a, b) {
					const aFirstName = get(a, 'contact.firstName');
					const aLastName = get(a, 'contact.lastName');
					const bFirstName = get(b, 'contact.firstName');
					const bLastName = get(b, 'contact.lastName');
					return sortByAlph(aFirstName + aLastName, bFirstName + bLastName);
				},
				render(bonus) {
					if (bonus.referral !== null && bonus.contact !== null) {
						return (
							<Link
								className="table-link"
								to={`/referrals/${bonus.referralId}`}
							>
								{bonus.contact.firstName + ' ' + bonus.contact.lastName}
							</Link>
						);
					}

					if (!bonus.referral && bonus.contact !== null) {
						return (
							<>{bonus.contact.firstName + ' ' + bonus.contact.lastName}</>
						);
					}
				},
			},
			{
				title: headers.bonusAmount,
				dataIndex: 'amountDue',
				key: 'amountDue',
				width: '11%',
				showSorterTooltip: false,
				sorter(a, b) {
					return sortByAlph(a.amountDue, b.amountDue);
				},
				render(amount, row) {
					const amountValue =
						amount > 0
							? amount.toString().replaceAll(/\B(?=(\d{3})+(?!\d))/g, ',')
							: amount;
					let userGroupCurrency = get(row, 'user.userGroup.currency');
					userGroupCurrency = userGroupCurrency ? userGroupCurrency : 'USD';
					let currency = get(row, 'currency');
					currency = currency ? currency : userGroupCurrency;
					const symbol = getSymbolFromCurrency(currency);
					const preStartUserGroupName = currentUser?.company?.userGroups.find(
						(group) => group.id === currentUser?.company.preStartUserGroupId
					)?.name;
					const preStartEnabled =
						currentUser?.company?.preStartEnabled &&
						preStartUserGroupName &&
						row?.origin === 'pre_start';

					return (
						<div
							className="text-green"
							style={{
								display: 'flex',
								alignItems: 'center',
								flexWrap: 'wrap',
								gap: '5px',
							}}
						>
							<span>
								{symbol}
								{amountValue}
							</span>
							{preStartEnabled && (
								<Tag color="green">{preStartUserGroupName}</Tag>
							)}
						</div>
					);
				},
			},
			{
				title: headers.elligibleDate,
				dataIndex: 'earnedDate',
				key: 'earnedDate',
				width: '10%',
				showSorterTooltip: false,
				sorter: (a, b) => sortByAlph(dayjs(b.earnedDate), dayjs(a.earnedDate)),
				render: (earnedDate) =>
					formatDate(
						earnedDate,
						currentUser.languageCode,
						currentUser.dateFormat
					),
			},
			{
				title: headers.payment,
				dataIndex: 'payment',
				key: 'payment',
				width: '9%',
				render: (payment, data) => (
					<>{getPaymentLink(payment, get(data, 'referralId'))}</>
				),
			},
			{
				title: headers.status,
				dataIndex: 'bonusStatus',
				key: 'bonusStatus',
				showSorterTooltip: false,
				sorter: (a, b) => sortByAlph(a.bonusStatus, b.bonusStatus),
				render: (bonusStatus, data) => {
					const managerBonuses = currentUser.company
						?.disableManagerPermissionsByType
						? JSON.parse(currentUser.company.disableManagerPermissionsByType)
								.managerBonuses
						: 'hidden';
					const isManagerPermissionDisabled = managerBonuses !== 'edit';
					const displayAS = get(currentUser, 'displayAs');
					const isStatusDropdownDisabled = Boolean(
						isManagerPermissionDisabled && displayAS === USER_ROLES.MANAGER
					);

					const menuItems = Object.keys(BONUS_STATUS).map((key) => {
						const isCurrentStatus = isPromptDisabled(bonusStatus, key);

						return {
							key,
							label: (
								<div
									onClick={(e) => {
										e.stopPropagation();
										if (!isCurrentStatus) {
											setStatusUpdateModal({
												visible: true,
												bonusData: data,
												targetStatus: key,
												statusColor: conditionalStatusStyle(key),
											});
											setVisibility((prev) => ({ ...prev, [data.id]: false }));
										}
									}}
									style={{
										cursor: isCurrentStatus ? 'not-allowed' : 'pointer',
										color: isCurrentStatus ? '#595959' : undefined,
									}}
								>
									{ml(
										mapBonusStatus(key, company),
										currentUser,
										allMultiLingualData
									)}
								</div>
							),
							style: conditionalStatusStyle(key),
							disabled: isCurrentStatus,
						};
					});

					return (
						<Dropdown
							key={data.id}
							trigger={['click']}
							style={{ marginRight: 5 }}
							open={visibility[data.id]}
							menu={{ items: menuItems }}
							disabled={isStatusDropdownDisabled}
							onOpenChange={(flag) => handleVisibleChange(data.id, flag)}
						>
							<Button type="link">
								<span style={conditionalStatusStyle(bonusStatus)}>
									{ml(
										mapBonusStatus(bonusStatus, company),
										currentUser,
										allMultiLingualData
									)}{' '}
								</span>
								<DownOutlined />
							</Button>
						</Dropdown>
					);
				},
			},
			{
				title: headers.details,
				key: 'details',
				render: (bonus) => {
					return (
						<Button
							type="primary"
							size="small"
							onClick={() => handleOpenDetailsModal(bonus)}
						>
							Details
						</Button>
					);
				},
			},
		],
		[
			headers,
			sortByAlph,
			currentUser,
			allMultiLingualData,
			getPaymentLink,
			conditionalStatusStyle,
			visibility,
			handleVisibleChange,
			handleOpenDetailsModal,
			ignoreEligibilityChecks,
		]
	);

	const locale = {
		emptyText: <Spinner company={company} />,
	};

	return (
		<>
			<Table
				pagination={{ pageSize: 25, showSizeChanger: false }}
				rowKey={(record) => record.id}
				dataSource={tableBonuses}
				columns={columns}
				locale={locale}
				scroll={{ x: 656 }}
				onChange={(_pagination, _filters, _sorter, extra) =>
					handleTableChange(extra)
				}
			/>

			{modalLoading && <Spinner company={company} />}

			{/* Status Update Modal */}
			<StatusUpdateModal
				visible={statusUpdateModal.visible}
				bonusData={statusUpdateModal.bonusData}
				targetStatus={statusUpdateModal.targetStatus}
				onCancel={handleModalCancel}
				onConfirm={handleModalConfirm}
				currentUser={currentUser}
				allMultiLingualData={allMultiLingualData}
				ignoreEligibilityChecks={ignoreEligibilityChecks}
				onIgnoreEligibilityChange={handleIgnoreEligibilityChange}
				form={form}
				awaitingResponse={awaitingResponse}
			/>

			{/* Bonus Details Modal */}
			{modalVisible && selectedBonus && (
				<BonusDetailsModal
					bonus={selectedBonus}
					theme={theme}
					currentUser={currentUser}
					awaitingResponse={awaitingResponse}
					allMultiLingualData={allMultiLingualData}
					onClose={handleCloseModal}
					fetchingEligibility={fetchingEligibility}
					fetchEligibilityResults={fetchEligibilityResults}
				/>
			)}
		</>
	);
};

const mapStateToProps = (state) => {
	return {
		fetchingEligibility: state.referrals.fetchingEligibility,
	};
};

const mapDispatchToProps = {
	updateBonusStatus,
	fetchEligibilityResults,
};

export default connect(mapStateToProps, mapDispatchToProps)(BonusTable);
